import styled from "@emotion/styled";
import { FC, memo, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { HelpDeskMono } from "@livechat/design-system-icons";
import { DesignToken, Icon, RadiusToken, SpacingToken } from "@livechat/design-system-react-components";

import { NAVIGATION_ITEMS } from "constants/navigation-items";
import { ResponsiveBreakpointType } from "constants/responsive-breakpoint";
import { TicketsRoutes } from "constants/tickets/TicketsRoutes";
import { NavigationItemInterface } from "interfaces/navigationItem";
import { IAgent } from "store/entities/agents";
import { IWidget } from "store/entities/applications";
import { selectFeatureToggleProductSwitcher } from "store/entities/license";
import { TicketsViewActions } from "store/views/tickets/actions";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";

import { NavigationItem } from "./NavigationItem";
import { NavigationMyProfile } from "./NavigationMyProfile";
import { INavigationProps as IProps } from "./interfaces";
import { ProductSwitcher } from "./product-switcher/ProductSwitcher";

export const DESKTOP_NAVIGATION_WIDTH = 54;

const itemsTop: NavigationItemInterface[] = NAVIGATION_ITEMS.filter(
  item => item.placement === "top" && item.breakpoints?.includes(ResponsiveBreakpointType.Desktop)
);

const itemsBottom: NavigationItemInterface[] = NAVIGATION_ITEMS.filter(
  item => item.placement === "bottom" && item.breakpoints?.includes(ResponsiveBreakpointType.Desktop)
);

const buildFullscreenWidgetsItems = (fullscreenWidgets: IWidget[]): NavigationItemInterface[] =>
  fullscreenWidgets.map(widget => ({
    id: widget.id,
    name: widget.name || "",
    icon: widget.icon,
    path: widget.path,
  }));

function canAccessItem(item: NavigationItemInterface, canAccessApp: boolean, agent: IAgent): boolean {
  return Boolean(
    (canAccessApp || item.activeWhenAppAccessNotAllowed) &&
      (!item.permissions || item.permissions.includes(agent.permission))
  );
}

const NavigationComponent: FC<IProps> = memo(
  ({ agent, canAccessApp, onLogout, fullscreenWidgets, toggleShortcutsModalDisplayed }) => {
    const dispatch = useDispatch();
    const productSwitcherEnabled = useSelector(selectFeatureToggleProductSwitcher);

    const fullscreenWidgetsItems = useMemo(() => buildFullscreenWidgetsItems(fullscreenWidgets), [fullscreenWidgets]);

    const handleLogoClick = (): void => {
      if (canAccessApp) {
        dispatch(TicketsViewActions.selectStandardView({ route: TicketsRoutes.All }));
      }
    };

    return (
      <Wrapper>
        <WrapperTop>
          {productSwitcherEnabled ? (
            <ProductSwitcher />
          ) : (
            <HelpDeskLogo onClick={handleLogoClick}>
              <Icon source={HelpDeskMono} size="large" />
            </HelpDeskLogo>
          )}
          {itemsTop.map(item => (
            <NavigationItem
              item={item}
              key={item.id}
              isMobile={false}
              accessAllowed={canAccessItem(item, canAccessApp, agent)}
            />
          ))}
          {fullscreenWidgetsItems.map(item => (
            <NavigationItem key={item.id} item={item} isMobile={false} isIntegration accessAllowed />
          ))}
        </WrapperTop>
        <WrapperBottom>
          {itemsBottom.map(item => (
            <NavigationItem
              key={item.id}
              item={item}
              isMobile={false}
              accessAllowed={canAccessItem(item, canAccessApp, agent)}
            />
          ))}

          <NavigationMyProfile
            agent={agent}
            onLogout={onLogout}
            toggleShortcutsModalDisplayed={toggleShortcutsModalDisplayed}
          />
        </WrapperBottom>
      </Wrapper>
    );
  }
);

NavigationComponent.displayName = "NavigationComponent";

const Wrapper = styled.div`
  display: flex;
  width: ${DESKTOP_NAVIGATION_WIDTH}px;
  flex-direction: column;
  background-color: ${withVar(DesignToken.NavbarBackground)};
`;

const WrapperTop = styled.nav`
  flex: 2 1 0%;
  overflow: auto;
  gap: 6px;
  padding-top: 6px;
  display: flex;
  flex-direction: column;
  z-index: ${StackingContextLevel.AppHeader};
  ::-webkit-scrollbar {
    display: none;
  }
`;

const WrapperBottom = styled.nav`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 6px;
  padding: 6px 0;
`;

const HelpDeskLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 6px;
  min-height: 42px;
  cursor: pointer;
  border-radius: ${withVar(RadiusToken.Radius3)};
  transition: all 0.2s ease-in-out 0s;
  svg {
    width: ${withVar(SpacingToken.Spacing6)};
    height: ${withVar(SpacingToken.Spacing6)};
    color: ${withVar(DesignToken.ContentLockedWhite)};
    opacity: 0.5;
  }
  &:hover {
    svg {
      width: ${withVar(SpacingToken.Spacing7)};
      height: ${withVar(SpacingToken.Spacing7)};
      opacity: 1;
    }
  }
`;

export default NavigationComponent;
